import React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { FormattedMessage } from 'react-intl';
import { IOrganizationDetailsDTO, IOrganizationsDAO } from '../../../../../api/organizations.dao';
import { IAPIErrorObject, IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { alertServerError, handleApiException } from '../../../../global/modal/alerts-host/alert-templates';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { EventExecutionRuntime } from '../../../../../domain/jobs/implementations/event-execution-runtime';
import { JobType } from '../../../../../api/jobs.dao';
import { IUserDetailsDTO, IUsersDAO } from '../../../../../api/users.dao';
import { OrganizationFormDataViewModel } from './organization-form-data.viewmodel';
import { convertBoolTypeForMixPanel, OpenFrom, TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';

class OrganizationFormVM {
    static getFieldsMatchedByList(fieldNames: string[], fields: IAPIErrorObject['fields']): { [key: string]: string } {
        return fields.reduce((match, { name, message }) => {
            if (fieldNames.includes(name)) match[name] = message;
            return match;
        }, {});
    }

    @mobx.observable
    public isSaving: boolean;
    @mobx.observable
    public usersList: IUserDetailsDTO[];

    @mobx.observable
    private organizationFormDataVM: OrganizationFormDataViewModel;
    @mobx.observable
    private initialOrganizationValues: IOrganizationDetailsDTO;

    private closeOrganizationPopup: () => void;

    public handleApiException(apiError: IAPIErrorObject): void {
        const fieldNames = this.organizationFormDataVM.getVmFieldNames();
        if (fieldNames.length && apiError?.fields?.length) {
            const fieldsMatch = OrganizationFormVM.getFieldsMatchedByList(fieldNames, apiError.fields);

            if (Reflect.ownKeys(fieldsMatch).length) {
                this.organizationFormDataVM.setFieldsInvalidByApi(fieldsMatch);
                return;
            }
        }

        this.organizationFormDataVM.setServerErrorMessage(apiError.errorMessage);
    }

    constructor(
        private updateOrganizationOnList: () => void,
        closeOrganizationPopup: () => void,
        private readonly accountId: number,
        private readonly eventId: number,
        private readonly organizationId: number | null,
        private handleOrganizationDeletion: () => Promise<void>,
        private openFrom: OpenFrom,
        @IoC.Inject private readonly organizationDAO: IOrganizationsDAO = IoC.Container.get(IOrganizationsDAO),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly executionRuntime: EventExecutionRuntime = IoC.Container.get(EventExecutionRuntime),
        @IoC.Inject private readonly usersDAO: IUsersDAO = IoC.Container.get(IUsersDAO),
        @IoC.Inject
        private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider)
    ) {
        this.isSaving = false;
        this.closeOrganizationPopup = closeOrganizationPopup;
        this.fetchUsersList();
    }

    public get deleteOrganizationJob() {
        return this.executionRuntime.getJobByType(JobType.DeleteOrganization);
    }

    public formSubmitAction = async (): Promise<boolean> => {
        if (!this.organizationFormDataVM.checkForm()) {
            return;
        }

        return await this.updateOrganizationCall();
    };

    public getOrganizationFormDataVM() {
        return this.organizationFormDataVM;
    }

    public fetchOrganizationDetails = async () => {
        try {
            const organizationDetails = await this.organizationDAO.getOrganizationDetails({
                accountId: this.accountId,
                eventId: this.eventId,
                organizationId: this.organizationId,
            });
            this.initialOrganizationValues = organizationDetails;
            mobx.runInAction(() => {
                this.organizationFormDataVM = new OrganizationFormDataViewModel({
                    name: organizationDetails.name,
                    organizationTicker: organizationDetails.organizationTicker,
                    organizationType: organizationDetails.organizationType,
                    externalOrganizationId: organizationDetails.externalOrganizationId,
                    approvedDomains: organizationDetails.approvedDomains,
                    participantLink: organizationDetails.participantLink,
                    presenterLink: organizationDetails.presenterLink,
                    chaperones: organizationDetails.chaperones,
                    priorityInEvent: organizationDetails.priorityInEvent
                });
            });
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError('API error fetching organization details', errorMessage);
        }
    };

    @mobx.action
    public async initialize() {
        if (this.organizationId) {
            await this.fetchOrganizationDetails();
        } else {
            this.organizationFormDataVM = new OrganizationFormDataViewModel({
                name: '',
                organizationTicker: '',
                organizationType: '',
                externalOrganizationId: '',
                chaperones: [],
                priorityInEvent: null
            });
        }
    }

    public getOrganizationId() {
        return this.organizationId;
    }

    public trackCreateOrganizationMixPanel = () => {
        const organizationData = { ...this.organizationFormDataVM.toAPIDTO() };

        this.trackingService.get().track(TRACKING_EVENTS.NEW_ORGANIZATION, {
            'Organization Name': convertBoolTypeForMixPanel(!!organizationData.name),
            Type: organizationData.organizationType,
            Ticker: convertBoolTypeForMixPanel(!!organizationData.organizationTicker),
            'External Organization ID': convertBoolTypeForMixPanel(!!organizationData.externalOrganizationId),
            'Priority in Event': convertBoolTypeForMixPanel(organizationData.priorityInEvent != null),
            Chaperone: organizationData.chaperones,
            'Approved domains': convertBoolTypeForMixPanel(organizationData.approvedDomains.length > 0),
            'Open From': this.openFrom,
        });
    };

    public trackEditOrganizationMixPanel = () => {
        const organizationData = { ...this.organizationFormDataVM.toAPIDTO() };

        this.trackingService.get().track(TRACKING_EVENTS.EDIT_ORGANIZATION, {
            'Organization Name': convertBoolTypeForMixPanel(
                this.initialOrganizationValues.name !== organizationData.name
            ),
            Type: organizationData.organizationType,
            Ticker: convertBoolTypeForMixPanel(
                this.initialOrganizationValues.organizationTicker !== organizationData.organizationTicker
            ),
            'External Organization ID': convertBoolTypeForMixPanel(
                organizationData.externalOrganizationId != null
                    ? this.initialOrganizationValues.externalOrganizationId !== organizationData.externalOrganizationId
                    : false
            ),
            'Priority in Event': convertBoolTypeForMixPanel(
                organizationData.priorityInEvent != null
                    ? this.initialOrganizationValues.priorityInEvent !== organizationData.priorityInEvent
                    : false
            ),
            Chaperone: organizationData.chaperones,
            "Presenter's Link": convertBoolTypeForMixPanel(
                this.initialOrganizationValues.presenterLink !== organizationData.presenterLink
            ),
            'Participant & Host Link': convertBoolTypeForMixPanel(
                this.initialOrganizationValues.participantLink !== organizationData.participantLink
            ),
            "Presenter's Link Copied": convertBoolTypeForMixPanel(
                this.organizationFormDataVM.getIsPresenterLinkCopied()
            ),
            'Participant & Host Link Copied': convertBoolTypeForMixPanel(
                this.organizationFormDataVM.getIsParticipantLinkCopied()
            ),
            'Approved domains': convertBoolTypeForMixPanel(
                (this.initialOrganizationValues.approvedDomains || []).some(
                    ({ domain }, i) => domain !== organizationData.approvedDomains?.[i]?.domain
                )
            ),
        });
    };

    @mobx.action
    private async updateOrganizationCall() {
        this.isSaving = true;
        try {
            if (this.organizationId != null) {
                await this.organizationDAO.updateOrganization({
                    ...this.organizationFormDataVM.toAPIDTO(),
                    accountId: this.accountId,
                    eventId: this.eventId,
                    organizationId: this.organizationId,
                });
                this.trackEditOrganizationMixPanel();
            } else {
                await this.organizationDAO.createOrganization({
                    ...this.organizationFormDataVM.toAPIDTO(),
                    accountId: this.accountId,
                    eventId: this.eventId,
                });

                this.trackCreateOrganizationMixPanel();
            }
            return this.onSaveCallback();
        } catch (e) {
            if (e.response?.status === 403) {
                handleApiException(e);
            } else {
                this.handleApiException(this.errorParsingService.extractAPIError(e));
            }
            return false;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    @mobx.action.bound
    private onSaveCallback() {
        this.updateOrganizationOnList();
        return true;
    }

    @mobx.action
    public fetchUsersList = async () => {
        try {
            const response = await this.usersDAO.getAccountUsers({ accountId: this.accountId, activeOnly: true});
            this.onFetchUsersListSuccess(response);
        } catch (error) {
            if (error.response?.status !== 403) {
                this.onFetchUsersListError(error);
            }
        }
    };

    @mobx.action
    private onFetchUsersListSuccess(users: IUserDetailsDTO[]): void {
        const formattedResponse = users.map(user => ({ ...user, name: `${user.firstName} ${user.lastName}` }));

        this.usersList = formattedResponse;
    }

    @mobx.action
    private onFetchUsersListError(error): void {
        this.alertsService.alert({
            message: this.errorParsingService.extractMessage(error)
        });
    }

    public deleteOrganization = () => {
        this.alertsService.confirm({
            message: (
                <>
                    <span className='alert-message' >
                        <FormattedMessage
                            id='oe.alerts.deleteOrganization.areYouSure'
                            description='Alert message in delete organization popup window'
                            defaultMessage='Are you sure you want to{space}'
                            values={{
                                space: ' '
                            }}
                        />
                        <span className='alert-message--danger'>
                            <FormattedMessage
                                id='oe.alerts.deleteOrganization.deleteThisOrganization'
                                description='Alert message in delete organization popup window'
                                defaultMessage='delete this organization{space}'
                                values={{
                                    space: ' '
                                }}
                            />
                        </ span >?
                    </span>
                    <br /><br />
                    <span className='alert-message'>
                        <FormattedMessage
                            id='oe.alerts.deleteOrganization.afterDeletion'
                            description='Alert message in delete organization popup window second part'
                            defaultMessage='All individual attendees who belong to this organization will also be deleted.'
                        />
                    </span> <br /><br />
                </>),
            confirmButtonProps: {
                text: <FormattedMessage
                    id='oe.general.yesDelete'
                    description='Label text'
                    defaultMessage='Yes, Delete'
                />,
                modifier: 'confirm-warn',
                handler: this.handleOrganizationDeletion,
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };
}

export { OrganizationFormVM };
